import React, { useEffect, useCallback, ReactNode } from 'react';
import { WeaveActionBarProvider } from '@frontend/action-bar';
import { closeTopBarPanelEvent } from '@frontend/panel-engine';
import { ScheduleSidePanels } from '@frontend/schedule';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { TaskCenterPanel } from '@frontend/task-center';
import { ChatbotAssistant } from '@frontend/weave-copilot';
import { SoftphoneManager } from '@frontend/weave-softphone-manager';
import { useEventListener } from '@frontend/design-system';
import { useApp } from '../../providers/app.provider';
import { ActionBarContentWrappper } from './components/action-bar-content';
import { HamburgerButtonWrapper } from './components/buttons/hamburger-button';
import { LocationNavButtonWrapper } from './components/buttons/location-button';
import { LogoButtonWrapper } from './components/buttons/logo-button';
import { SettingsButtonWrapper } from './components/buttons/settings-button';
import { TaskTrayButton } from './components/buttons/task-tray-button';
import { TopBarMobileControlsWrapper } from './components/mobile-controls';
import { NotificationButtonWrapper } from './components/notifications/notification-button';
import { NotificationsPanelWrapper } from './components/notifications/notifications-panel';
import { DialpadWrapper } from './components/phones/dialpad';
import { SoftphoneWrapper } from './components/phones/softphone-wrap';
import { DesktopNavList, SectionFlex, HeaderRoot, DevBadgeWrapper } from './components/primitives';
import { ProfileButtonWrapper } from './components/profile/profile-button';
import { QuickFillPanelWrapper } from './components/quick-fill/quick-fill-panel';
import { SchedulePulsePanelWrapper } from './components/schedule-pulse/schedule-pulse-panel';
import { dispatchTopBarModalClose } from './events/modal-events';
import { useTopBarSelection } from './hooks/use-top-bar-selection';
import { useVersionUpdates } from './hooks/use-version-updates';
import { UniversalSearchView } from './search/universal-search-view';
import { TopBarTray } from './top-bar-tray';

export const TopBar = () => {
  return (
    <TopBarOuter>
      <TopBarProviders>
        <HeaderRoot>
          <SectionFlex>
            <HamburgerButtonWrapper />
            <LogoButtonWrapper />
            <DialpadWrapper />
            <UniversalSearchView />
            <AIOnboardingAssistantWrapper />
            <DesktopNavList>
              <DevBadgeWrapper />
              <LocationNavButtonWrapper />
              <NotificationButtonWrapper />
              <SettingsButtonWrapper />
              <ProfileButtonWrapper />
              <TaskTrayButton />
            </DesktopNavList>
            <TopBarMobileControlsWrapper />
          </SectionFlex>
        </HeaderRoot>
        <SoftphoneWrapper />
        <ActionBarContentWrappper />
        <TopBarTray />
        <NotificationsPanelWrapper />
        <SchedulePulsePanelWrapper />
        <QuickFillPanelWrapper />
        <TaskCenterPanel />
        <ScheduleSidePanels />
      </TopBarProviders>
    </TopBarOuter>
  );
};

const TopBarProviders = ({ children }: { children: React.ReactNode }) => {
  return (
    <WeaveActionBarProvider>
      <SoftphoneManagerWrapper>{children}</SoftphoneManagerWrapper>
    </WeaveActionBarProvider>
  );
};

const SoftphoneManagerWrapper = ({ children }: { children: ReactNode }) => {
  const { setShouldPreventUpdate } = useApp();
  const onActiveCall = useCallback(() => {
    setShouldPreventUpdate(true);
  }, [setShouldPreventUpdate]);
  const onAllCallsEnded = useCallback(() => {
    setShouldPreventUpdate(false);
  }, [setShouldPreventUpdate]);

  return (
    <SoftphoneManager onAllCallsEnded={onAllCallsEnded} onCallIsActive={onActiveCall}>
      {children}
    </SoftphoneManager>
  );
};

const AIOnboardingAssistantWrapper = () => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showWeaveChatbotPOC = getFeatureFlagValue('weave-chatbot-poc');

  return showWeaveChatbotPOC && <ChatbotAssistant />;
};

const TopBarOuter = ({ children }: { children: ReactNode }) => {
  const { selectedLocationIds } = useAppScopeStore();

  useTopBarSelection();
  useEffect(() => {
    dispatchTopBarModalClose();
  }, [selectedLocationIds]);

  useVersionUpdates();
  useEventListener(closeTopBarPanelEvent as any, dispatchTopBarModalClose, true);

  return <React.Fragment>{children}</React.Fragment>;
};
